import {
  DocumentData,
  QueryConstraint,
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  where
} from "firebase/firestore";
import { db } from "../lib/firebase";
import { ADMIN_ANALYTICS_COLLECTION, ADMIN_ANALYTICS_DOCS, COLLECTIONS } from "./collectionNames";
import { adminCollection, logDevelopmentError, stringField } from "./firestoreHelpers";

export type FounderProductQualityStatus = "healthy" | "watch" | "action-needed" | "unavailable";

export type FounderProductQualityMetricId =
  | "crashFreePercent"
  | "averageLaunchTime"
  | "averageFeedLoad"
  | "averageDiscoverLoad"
  | "searchLatency"
  | "bookingSuccessRate"
  | "paymentSuccessRate"
  | "notificationSuccess"
  | "mediaUploadSuccess"
  | "messageDeliverySuccess";

export type FounderProductQualityMetric = {
  id: FounderProductQualityMetricId;
  title: string;
  value: string;
  story: string;
  detail: string;
  source: string;
  status: FounderProductQualityStatus;
  unavailableReason: string | null;
};

export type FounderProductQualityDashboard = {
  currentHealth: {
    title: string;
    value: string;
    story: string;
    status: FounderProductQualityStatus;
  };
  metrics: FounderProductQualityMetric[];
  lastUpdated: Date;
  error: string | null;
};

type PlatformHealthMetrics = Record<string, unknown>;

const PLATFORM_HEALTH_SOURCE = `${ADMIN_ANALYTICS_COLLECTION}/${ADMIN_ANALYTICS_DOCS.platformHealthMetrics}`;
const RECENT_SAMPLE_LIMIT = 500;

function startOfRecentWindow(days: number) {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date;
}

function metric(
  id: FounderProductQualityMetricId,
  title: string,
  value: string,
  story: string,
  detail: string,
  source: string,
  status: FounderProductQualityStatus,
  unavailableReason: string | null = null
): FounderProductQualityMetric {
  return {
    id,
    title,
    value,
    story,
    detail,
    source,
    status,
    unavailableReason
  };
}

function unavailableMetric(id: FounderProductQualityMetricId, title: string, story: string, detail: string, source = PLATFORM_HEALTH_SOURCE) {
  return metric(id, title, "Unavailable", story, detail, source, "unavailable", detail);
}

function numericMetric(metrics: PlatformHealthMetrics, keys: string[]) {
  for (const key of keys) {
    const value = metrics[key];
    if (typeof value === "number" && Number.isFinite(value)) {
      return value;
    }
    if (typeof value === "string") {
      const parsed = Number(value.replace(/[$,%\s,]/g, ""));
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }
  }

  return null;
}

function normalizeRate(value: number | null) {
  if (value === null) return null;
  return value > 1 ? value / 100 : value;
}

function formatPercent(value: number | null) {
  return value === null ? "Unavailable" : new Intl.NumberFormat(undefined, { style: "percent", maximumFractionDigits: 1 }).format(value);
}

function formatMilliseconds(value: number | null) {
  if (value === null) return "Unavailable";
  if (value >= 1000) {
    return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 }).format(value / 1000)}s`;
  }
  return `${Math.round(value)}ms`;
}

function rateStatus(value: number | null): FounderProductQualityStatus {
  if (value === null) return "unavailable";
  if (value >= 0.99) return "healthy";
  if (value >= 0.95) return "watch";
  return "action-needed";
}

function latencyStatus(value: number | null, healthyMs: number, watchMs: number): FounderProductQualityStatus {
  if (value === null) return "unavailable";
  if (value <= healthyMs) return "healthy";
  if (value <= watchMs) return "watch";
  return "action-needed";
}

function statusLabel(status: FounderProductQualityStatus) {
  switch (status) {
    case "healthy":
      return "Healthy";
    case "watch":
      return "Watch";
    case "action-needed":
      return "Action Needed";
    case "unavailable":
      return "Unavailable";
  }
}

async function fetchPlatformHealthMetrics(): Promise<PlatformHealthMetrics> {
  try {
    const snapshot = await getDoc(doc(db, ADMIN_ANALYTICS_COLLECTION, ADMIN_ANALYTICS_DOCS.platformHealthMetrics));
    return snapshot.exists() ? snapshot.data() : {};
  } catch (error) {
    logDevelopmentError(error);
    return {};
  }
}

async function recentDocs(collectionName: string, constraints: QueryConstraint[] = []) {
  try {
    const snapshot = await getDocs(
      query(adminCollection(collectionName), ...constraints, orderBy("createdAt", "desc"), limit(RECENT_SAMPLE_LIMIT))
    );
    return snapshot.docs.map((document) => document.data());
  } catch (error) {
    logDevelopmentError(error);
    return [];
  }
}

async function recentCollectionGroupDocs(groupName: string, constraints: QueryConstraint[] = []) {
  try {
    const snapshot = await getDocs(query(collectionGroup(db, groupName), ...constraints, orderBy("createdAt", "desc"), limit(RECENT_SAMPLE_LIMIT)));
    return snapshot.docs.map((document) => document.data());
  } catch (error) {
    logDevelopmentError(error);
    return [];
  }
}

function successRateFromStatuses(documents: DocumentData[], successStatuses: string[], failureStatuses: string[]) {
  const successes = new Set(successStatuses);
  const failures = new Set(failureStatuses);
  let success = 0;
  let failure = 0;

  documents.forEach((data) => {
    const status = (stringField(data, "status") ?? stringField(data, "deliveryStatus") ?? stringField(data, "paymentStatus") ?? "").toLowerCase();
    if (successes.has(status)) {
      success += 1;
    } else if (failures.has(status)) {
      failure += 1;
    }
  });

  const total = success + failure;
  return total === 0 ? null : success / total;
}

async function bookingSuccessFallback() {
  const documents = await recentDocs(COLLECTIONS.bookings, [where("createdAt", ">=", startOfRecentWindow(30))]);
  return successRateFromStatuses(documents, ["confirmed", "completed"], ["payment_failed", "failed", "cancelled", "canceled", "declined", "expired"]);
}

async function paymentSuccessFallback() {
  const documents = await recentDocs(COLLECTIONS.marketplaceTransactions, [where("createdAt", ">=", startOfRecentWindow(30))]);
  return successRateFromStatuses(documents, ["paid", "completed"], ["failed", "cancelled", "canceled"]);
}

async function notificationSuccessFallback() {
  const documents = await recentDocs(COLLECTIONS.notifications, [where("createdAt", ">=", startOfRecentWindow(30))]);
  return successRateFromStatuses(documents, ["sent", "delivered", "read"], ["failed", "bounced"]);
}

async function messageDeliveryFallback() {
  const documents = await recentCollectionGroupDocs("messages", [where("createdAt", ">=", startOfRecentWindow(30))]);
  return successRateFromStatuses(documents, ["sent", "delivered", "read"], ["failed", "blocked"]);
}

function productHealth(metrics: FounderProductQualityMetric[]) {
  if (metrics.some((item) => item.status === "action-needed")) {
    return {
      title: "Current Product Health",
      value: "Action Needed",
      story: "One or more product-quality signals need immediate attention.",
      status: "action-needed" as const
    };
  }

  if (metrics.some((item) => item.status === "watch" || item.status === "unavailable")) {
    return {
      title: "Current Product Health",
      value: "Watch",
      story: "Chor is usable, but at least one quality signal needs monitoring or telemetry.",
      status: "watch" as const
    };
  }

  return {
    title: "Current Product Health",
    value: "Healthy",
    story: "Core product-quality signals are within the healthy range.",
    status: "healthy" as const
  };
}

function rateMetric(id: FounderProductQualityMetricId, title: string, rate: number | null, storySubject: string, source: string, unavailableReason: string) {
  if (rate === null) {
    return unavailableMetric(id, title, `${storySubject} needs telemetry before it can be judged.`, unavailableReason, source);
  }

  const status = rateStatus(rate);
  return metric(
    id,
    title,
    formatPercent(rate),
    `${storySubject} is ${formatPercent(rate).toLowerCase()}.`,
    status === "healthy" ? "Healthy range is 99% or better." : status === "watch" ? "Watch range is 95-99%." : "Below 95% requires attention.",
    source,
    status
  );
}

function latencyMetric(id: FounderProductQualityMetricId, title: string, valueMs: number | null, storySubject: string, healthyMs: number, watchMs: number) {
  if (valueMs === null) {
    return unavailableMetric(id, title, `${storySubject} needs telemetry before it can be judged.`, `${title} is not available in platform health telemetry.`);
  }

  const status = latencyStatus(valueMs, healthyMs, watchMs);
  return metric(
    id,
    title,
    formatMilliseconds(valueMs),
    `${storySubject} averages ${formatMilliseconds(valueMs)}.`,
    `Healthy target is ${formatMilliseconds(healthyMs)} or faster. Watch threshold is ${formatMilliseconds(watchMs)}.`,
    PLATFORM_HEALTH_SOURCE,
    status
  );
}

export async function fetchFounderProductQualityDashboard(): Promise<FounderProductQualityDashboard> {
  const platformMetrics = await fetchPlatformHealthMetrics();
  const [bookingFallback, paymentFallback, notificationFallback, messageFallback] = await Promise.all([
    bookingSuccessFallback(),
    paymentSuccessFallback(),
    notificationSuccessFallback(),
    messageDeliveryFallback()
  ]);

  const crashFree = normalizeRate(numericMetric(platformMetrics, ["crashFreePercent", "crashFreeRate", "crashFreeUsersPercent"]));
  const launchMs = numericMetric(platformMetrics, ["averageLaunchTimeMs", "avgLaunchTimeMs", "launchTimeMs"]);
  const feedMs = numericMetric(platformMetrics, ["averageFeedLoadMs", "avgFeedLoadMs", "feedLoadMs"]);
  const discoverMs = numericMetric(platformMetrics, ["averageDiscoverLoadMs", "avgDiscoverLoadMs", "discoverLoadMs"]);
  const searchMs = numericMetric(platformMetrics, ["searchLatencyMs", "averageSearchLatencyMs", "avgSearchLatencyMs"]);
  const bookingRate = normalizeRate(numericMetric(platformMetrics, ["bookingSuccessRate", "bookingSuccessPercent"]) ?? bookingFallback);
  const paymentRate = normalizeRate(numericMetric(platformMetrics, ["paymentSuccessRate", "paymentSuccessPercent"]) ?? paymentFallback);
  const notificationRate = normalizeRate(numericMetric(platformMetrics, ["notificationSuccessRate", "notificationSuccessPercent"]) ?? notificationFallback);
  const mediaUploadRate = normalizeRate(numericMetric(platformMetrics, ["mediaUploadSuccessRate", "mediaUploadSuccessPercent"]));
  const messageDeliveryRate = normalizeRate(numericMetric(platformMetrics, ["messageDeliverySuccessRate", "messageDeliverySuccessPercent"]) ?? messageFallback);

  const metrics: FounderProductQualityMetric[] = [
    rateMetric("crashFreePercent", "Crash Free %", crashFree, "Crash-free usage", PLATFORM_HEALTH_SOURCE, "Crash-free telemetry is not configured."),
    latencyMetric("averageLaunchTime", "Average Launch Time", launchMs, "Launch", 1500, 2500),
    latencyMetric("averageFeedLoad", "Average Feed Load", feedMs, "Feed loading", 1000, 1800),
    latencyMetric("averageDiscoverLoad", "Average Discover Load", discoverMs, "Discover loading", 1200, 2000),
    latencyMetric("searchLatency", "Search Latency", searchMs, "Search", 500, 1000),
    rateMetric("bookingSuccessRate", "Booking Success Rate", bookingRate, "Booking success", "platformHealthMetrics or bookings.createdAt sample", "Booking success needs platform telemetry or recent booking status records."),
    rateMetric("paymentSuccessRate", "Payment Success Rate", paymentRate, "Payment success", "platformHealthMetrics or marketplace_transactions.createdAt sample", "Payment success needs platform telemetry or recent marketplace transaction statuses."),
    rateMetric("notificationSuccess", "Notification Success", notificationRate, "Notification success", "platformHealthMetrics or notifications.createdAt sample", "Notification success needs platform telemetry or notification delivery statuses."),
    rateMetric("mediaUploadSuccess", "Media Upload Success", mediaUploadRate, "Media upload success", PLATFORM_HEALTH_SOURCE, "Media upload success telemetry is not configured."),
    rateMetric("messageDeliverySuccess", "Message Delivery Success", messageDeliveryRate, "Message delivery", "platformHealthMetrics or collectionGroup(messages).createdAt sample", "Message delivery needs platform telemetry or recent message delivery statuses.")
  ];

  return {
    currentHealth: productHealth(metrics),
    metrics,
    lastUpdated: new Date(),
    error: null
  };
}
